// Backlog #13: form-template channels. Shared by the API (authoritative validation) and the web form composer.
import type { ListSection } from './formSections';

export const FORM_FIELD_TYPES = [
  'short_text', 'long_text', 'number', 'single_select', 'multi_select', 'date', 'time', 'datetime', 'checkbox', 'file', 'person', 'building', 'department', 'group',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];
/** t693u: fields whose value is a list of workspace ids shown by name (people, buildings, departments, custom groups). */
export const PICKER_FIELD_TYPES = ['person', 'building', 'department', 'group'] as const;
export type PickerFieldType = (typeof PICKER_FIELD_TYPES)[number];
export const isPickerField = (t: FormFieldType): t is PickerFieldType => (PICKER_FIELD_TYPES as readonly string[]).includes(t);

export const FORM_FIELD_LABELS: Record<FormFieldType, string> = {
  short_text: 'Short text', long_text: 'Long text', number: 'Number', single_select: 'Single select', multi_select: 'Multi select',
  date: 'Date', time: 'Time', datetime: 'Date and time', checkbox: 'Checkbox', file: 'File / photo upload', person: 'Person', building: 'Building / site',
  department: 'Department', group: 'Group',
};

/**
 * Value shapes: text/select/date ('YYYY-MM-DD')/time ('HH:MM')/datetime (UK local 'YYYY-MM-DDTHH:MM') are strings,
 * number is a number, checkbox a boolean, multi_select / file / person / building / department / group are string arrays (ids for all but multi_select;
 * department and group hold the team id).
 */
export type FormValue = string | number | boolean | string[];
export type FormValues = Record<string, FormValue>;

export interface FormField {
  /** Stable key used in stored values; never changes when the label is edited. */
  id: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  help?: string | null;
  placeholder?: string | null;
  default?: FormValue | null;
  /** single_select / multi_select */
  options?: string[];
  /** number: bounds; multi_select/file/person/building/department/group: max items (pickers default 1) */
  min?: number | null;
  max?: number | null;
  /** number: unit shown after the value, e.g. "pallets" */
  unit?: string | null;
  /** person / building / department / group: allow more than one */
  multiple?: boolean;
  /** t518u building: start with the poster's own building (filled by the composer, and by the API when left empty). */
  default_own_building?: boolean;
}

export interface FormStatusOption { key: string; label: string; color: string }

export interface FormTemplate {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  emoji: string | null;
  version: number;
  fields: FormField[];
  /** t725u: section headings between the fields (never answered). */
  sections?: ListSection[];
  statuses: FormStatusOption[];
  default_status: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  channels?: { id: string; slug: string | null }[];
  submission_count?: number;
}

/** A form post as rendered in chat (fields are the template version the post was made with). */
export interface FormSubmissionView {
  id: string;
  template_id: string;
  template_name: string;
  emoji: string | null;
  version: number;
  latest_version: number;
  fields: FormField[];
  /** t725u: the version's section headings. */
  sections?: ListSection[];
  values: FormValues;
  /** Display text for ids (people, buildings, files) so clients need no extra lookups. */
  labels: Record<string, string>;
  status: string | null;
  statuses: FormStatusOption[];
  status_changed_at: string | null;
  status_changed_by: string | null;
}

export interface ChannelFormInfo {
  template: FormTemplate | null;
  can_assign: boolean;
  can_set_status: boolean;
  /** t518u: the viewer's own building (fills building fields set to "Default to the poster's building"). */
  own_building_id?: string | null;
  /** t518u: submissions create journeys (the link is on and complete). */
  creates_journeys?: boolean;
}

export interface FormSubmissionRow {
  id: string;
  message_id: string;
  sender_id: string;
  sender_name: string;
  created_at: string;
  edited_at: string | null;
  version: number;
  status: string | null;
  values: FormValues;
  labels: Record<string, string>;
  reply_count: number;
}

export const DEFAULT_FORM_STATUS_COLORS = ['#64748b', '#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed'];
export const VEHICLE_SIZES = ['Car', 'Small van', 'Large van', '7.5t lorry', '18t lorry', 'Artic (44t)'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const FIELD_ID_RE = /^[a-z][a-z0-9_]{0,39}$/;
export const FORM_LIMITS = { fields: 40, options: 50, optionLength: 100, label: 120, help: 300, shortText: 300, longText: 5000, files: 10, people: 20 } as const;

function validDate(s: string): boolean {
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

/** Same JSON ignoring object key order (jsonb hands keys back sorted), e.g. to tell whether a template's fields really changed. */
export function sameJson(a: unknown, b: unknown): boolean {
  const norm = (v: unknown): unknown => Array.isArray(v) ? v.map(norm)
    : v && typeof v === 'object' ? Object.fromEntries(Object.keys(v).sort().filter(k => (v as Record<string, unknown>)[k] !== undefined).map(k => [k, norm((v as Record<string, unknown>)[k])])) : v;
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

/** Snake-case id from a label, unique among `taken`. */
export function fieldIdFromLabel(label: string, taken: Iterable<string> = []): string {
  const used = new Set(taken);
  let base = label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32) || 'field';
  if (!/^[a-z]/.test(base)) base = `f_${base}`.slice(0, 32);
  let id = base;
  for (let i = 2; used.has(id); i++) id = `${base}_${i}`;
  return id;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
}

function maxItems(f: FormField): number {
  if (isPickerField(f.type)) return f.multiple ? Math.min(f.max ?? FORM_LIMITS.people, FORM_LIMITS.people) : 1;
  if (f.type === 'file') return Math.min(f.max ?? FORM_LIMITS.files, FORM_LIMITS.files);
  if (f.type === 'multi_select') return f.max ?? FORM_LIMITS.options;
  return 1;
}

/** Validates and normalises a single value; returns [value | undefined (=empty), error]. */
export function normaliseFormValue(f: FormField, raw: unknown): [FormValue | undefined, string | null] {
  if (f.type === 'checkbox') {
    const v = raw === true || raw === 'true';
    if (raw !== undefined && raw !== null && typeof raw !== 'boolean' && raw !== 'true' && raw !== 'false') return [undefined, 'Must be yes or no'];
    if (f.required && !v) return [v, 'Must be ticked'];
    return [v, null];
  }
  if (isEmpty(raw)) return [undefined, f.required ? 'Required' : null];
  switch (f.type) {
    case 'short_text':
    case 'long_text': {
      if (typeof raw !== 'string') return [undefined, 'Must be text'];
      const v = f.type === 'short_text' ? raw.trim().replace(/\s+/g, ' ') : raw.trim();
      const max = f.type === 'short_text' ? FORM_LIMITS.shortText : FORM_LIMITS.longText;
      return v.length > max ? [undefined, `At most ${max} characters`] : [v, null];
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.trim()) : NaN;
      if (!Number.isFinite(n)) return [undefined, 'Must be a number'];
      if (f.min != null && n < f.min) return [undefined, `Must be at least ${f.min}`];
      if (f.max != null && n > f.max) return [undefined, `Must be at most ${f.max}`];
      return [n, null];
    }
    case 'single_select':
      if (typeof raw !== 'string' || !(f.options ?? []).includes(raw)) return [undefined, 'Choose one of the options'];
      return [raw, null];
    case 'date':
      return typeof raw === 'string' && validDate(raw) ? [raw, null] : [undefined, 'Must be a date'];
    case 'time':
      return typeof raw === 'string' && TIME_RE.test(raw) ? [raw, null] : [undefined, 'Must be a time (HH:MM)'];
    case 'datetime': {
      const s = typeof raw === 'string' ? raw.slice(0, 16) : '';
      return validDate(s.slice(0, 10)) && s[10] === 'T' && TIME_RE.test(s.slice(11)) ? [s, null] : [undefined, 'Must be a date and time'];
    }
    case 'multi_select':
    case 'file':
    case 'person':
    case 'building':
    case 'department':
    case 'group': {
      const arr = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : null;
      if (!arr || arr.some(x => typeof x !== 'string')) return [undefined, 'Invalid value'];
      const v = [...new Set(arr as string[])];
      if (f.type === 'multi_select') {
        if (v.some(x => !(f.options ?? []).includes(x))) return [undefined, 'Choose from the options'];
        if (f.min != null && v.length < f.min) return [undefined, `Choose at least ${f.min}`];
      } else if (v.some(x => !UUID_RE.test(x))) return [undefined, 'Invalid selection'];
      if (v.length > maxItems(f)) return [undefined, maxItems(f) === 1 ? 'Choose one' : `At most ${maxItems(f)}`];
      return [v, null];
    }
  }
  return [undefined, 'Unknown field type'];
}

/** Validates a whole submission. Unknown keys are dropped. */
export function validateFormValues(fields: FormField[], raw: Record<string, unknown>): { values: FormValues; errors: Record<string, string> } {
  const values: FormValues = {};
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const [v, err] = normaliseFormValue(f, raw?.[f.id]);
    if (err) errors[f.id] = err;
    else if (v !== undefined) values[f.id] = v;
  }
  return { values, errors };
}

/** Builder-side validation of a template's field list (also enforced by the API). */
export function validateFormFields(fields: FormField[]): string[] {
  const errs: string[] = [];
  if (!Array.isArray(fields) || fields.length === 0) return ['Add at least one field'];
  if (fields.length > FORM_LIMITS.fields) errs.push(`At most ${FORM_LIMITS.fields} fields`);
  const ids = new Set<string>();
  fields.forEach((f, i) => {
    const n = `Field ${i + 1}${f?.label ? ` (“${f.label}”)` : ''}`;
    if (!f || typeof f !== 'object') { errs.push(`${n}: invalid`); return; }
    if (!FIELD_ID_RE.test(f.id ?? '')) errs.push(`${n}: invalid id`);
    else if (ids.has(f.id)) errs.push(`${n}: duplicate id ${f.id}`);
    ids.add(f.id);
    if (!(FORM_FIELD_TYPES as readonly string[]).includes(f.type)) errs.push(`${n}: unknown type`);
    if (typeof f.label !== 'string' || !f.label.trim() || f.label.length > FORM_LIMITS.label) errs.push(`${n}: label must be 1-${FORM_LIMITS.label} characters`);
    if (f.help && f.help.length > FORM_LIMITS.help) errs.push(`${n}: help text too long`);
    if (f.type === 'single_select' || f.type === 'multi_select') {
      const o = f.options ?? [];
      if (o.length === 0) errs.push(`${n}: add at least one option`);
      if (o.length > FORM_LIMITS.options) errs.push(`${n}: too many options`);
      if (o.some(x => typeof x !== 'string' || !x.trim() || x.length > FORM_LIMITS.optionLength)) errs.push(`${n}: options must be 1-${FORM_LIMITS.optionLength} characters`);
      if (new Set(o).size !== o.length) errs.push(`${n}: duplicate options`);
    }
    if (f.type === 'number' && f.min != null && f.max != null && f.min > f.max) errs.push(`${n}: minimum is above maximum`);
    if (f.default !== undefined && f.default !== null && !isEmpty(f.default)) {
      if (f.type === 'file') errs.push(`${n}: file fields cannot have a default`);
      else {
        const [, e] = normaliseFormValue({ ...f, required: false }, f.default);
        if (e) errs.push(`${n}: default value - ${e.toLowerCase()}`);
      }
    }
  });
  return errs;
}

export function validateStatusOptions(statuses: FormStatusOption[], defaultStatus: string | null): string[] {
  const errs: string[] = [];
  if (statuses.length > 12) errs.push('At most 12 statuses');
  const keys = new Set<string>();
  for (const s of statuses) {
    if (!FIELD_ID_RE.test(s.key)) errs.push(`Status “${s.label}”: invalid key`);
    if (keys.has(s.key)) errs.push(`Status “${s.label}”: duplicate`);
    keys.add(s.key);
    if (!s.label?.trim() || s.label.length > 40) errs.push('Status labels must be 1-40 characters');
    if (!/^#[0-9a-f]{6}$/i.test(s.color)) errs.push(`Status “${s.label}”: colour must be #rrggbb`);
  }
  if (defaultStatus && !keys.has(defaultStatus)) errs.push('Default status must be one of the statuses');
  return errs;
}

/** Initial values for a new submission (template defaults). */
export function formDefaults(fields: FormField[], ctx: { own_building_id?: string | null } = {}): FormValues {
  const out: FormValues = {};
  for (const f of fields) {
    if (f.default !== undefined && f.default !== null && !isEmpty(f.default)) out[f.id] = f.default;
    else if (f.type === 'building' && f.default_own_building && ctx.own_building_id) out[f.id] = [ctx.own_building_id];
  }
  return out;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Thu 15 Oct 2026" without timezone conversion (the value is a calendar date). */
export function formatFormDate(d: string): string {
  const m = DATE_RE.exec(d);
  if (!m) return d;
  const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return `${DAYS[dt.getUTCDay()]} ${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}`;
}

/** Human-readable value; ids are resolved through `labels`. */
export function formatFormValue(f: FormField, v: FormValue | undefined, labels: Record<string, string> = {}): string {
  if (v === undefined || v === null) return '';
  switch (f.type) {
    case 'checkbox': return v ? 'Yes' : 'No';
    case 'number': return `${v}${f.unit ? ` ${f.unit}` : ''}`;
    case 'date': return formatFormDate(String(v));
    case 'datetime': return `${formatFormDate(String(v).slice(0, 10))} ${String(v).slice(11, 16)}`;
    case 'multi_select': return (v as string[]).join(', ');
    case 'file': case 'person': case 'building': case 'department': case 'group': return (v as string[]).map(id => labels[id] ?? 'Unknown').join(', ');
    default: return String(v);
  }
}

/**
 * Plain-text body stored on the chat message. It is what full-text search, the AI assistant, notifications,
 * "convert to task" and older clients see. The first line is a one-line summary.
 */
export function renderFormText(templateName: string, fields: FormField[], values: FormValues, labels: Record<string, string> = {}): string {
  const shown = fields.filter(f => values[f.id] !== undefined && !(f.type === 'checkbox' && values[f.id] === false));
  const summary = shown.filter(f => f.type !== 'long_text' && f.type !== 'file' && f.type !== 'checkbox').slice(0, 2)
    .map(f => formatFormValue(f, values[f.id], labels)).filter(Boolean);
  const lines = [`${templateName}${summary.length ? `: ${summary.join(' · ')}` : ''}`];
  for (const f of shown) lines.push(`${f.label}: ${formatFormValue(f, values[f.id], labels)}`);
  return lines.join('\n').slice(0, 19000);
}

/** Comparable key for sorting submissions by a field. */
export function formSortKey(f: FormField | undefined, v: FormValue | undefined, labels: Record<string, string>): string | number {
  if (v === undefined) return '';
  if (!f) return String(v);
  if (f.type === 'number') return Number(v);
  if (f.type === 'checkbox') return v ? 1 : 0;
  if (f.type === 'date' || f.type === 'time' || f.type === 'datetime') return String(v);
  return formatFormValue(f, v, labels).toLowerCase();
}
